import {CdkDragEnd, CdkDragMove, CdkDragStart} from '@angular/cdk/drag-drop';
import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ResizedEvent} from '@ngx-seatback/utils';
import {FixedAspectRatioImageDTO} from 'src/app/services/api/api';
import {Bounds, Clipping, Coordinate2D, Corner, HTMLBounds, Rect} from '../model';


@Component({
  selector: 'image-cropper',
  templateUrl: './image-cropper.component.html',
  styleUrls: ['./image-cropper.component.scss'],
  standalone: false,
})
export class ImageCropperComponent implements OnInit {
  get initialClipping(): Clipping {
    return this._initialClipping;
  }

  @Input()
  set initialClipping(value: Clipping) {
    this._initialClipping = value;
    this._clipping = value;
    this.refreshDimensionsFromClipping();
  }

  public get clipping(): Clipping {
    return this._clipping;
  }

  @Input()
  get image(): FixedAspectRatioImageDTO {
    return this._image;
  }

  set image(value: FixedAspectRatioImageDTO) {
    this._image = value;
  }

  get containerWidth() {
    if (!this.overlay?.nativeElement) {
      return;
    }
    return this.overlay.nativeElement.getBoundingClientRect().width;
  }

  get containerHeight() {
    if (!this.overlay?.nativeElement) {
      return;
    }
    return this.overlay.nativeElement.getBoundingClientRect().height;
  }

  get leftOverlay(): HTMLBounds {
    if (!this.cropArea || !this.overlay?.nativeElement) {
      return;
    }
    return {
      left: 0,
      top: this.cropArea.topLeft.y,
      right: this.containerWidth - this.cropArea.topLeft.x,
      bottom: this.containerHeight - this.cropArea.bottomLeft.y,
    };
  }

  get topOverlay(): HTMLBounds {
    if (!this.cropArea || !this.overlay?.nativeElement) {
      return;
    }
    return { left: 0, top: 0, right: 0, bottom: this.containerHeight - this.cropArea.topLeft.y };
  }

  get rightOverlay(): HTMLBounds {
    if (!this.cropArea || !this.overlay?.nativeElement) {
      return;
    }
    return {
      left: this.cropArea.topRight.x,
      top: this.cropArea.topRight.y,
      right: 0,
      bottom: this.containerHeight - this.cropArea.bottomRight.y,
    };
  }

  get bottomOverlay(): HTMLBounds {
    if (!this.cropArea || !this.overlay?.nativeElement) {
      return;
    }
    return { left: 0, top: this.cropArea.bottomLeft.y, right: 0, bottom: 0 };
  }

  get position() {
    if (this.moveDragging) {
      return;
    }
    return this.cropArea ? { x: this.cropArea.x, y: this.topLeftPos.y } : { x: 0, y: 0 };
  }

  get leftPos(): Coordinate2D {
    if (this.dragging === 'left') {
      return;
    }
    return this.cropArea
      ? {
          x: this.cropArea.topLeft.x,
          y: this.cropArea.topLeft.y + this.cropArea.height / 2 - this.handleSize / 2,
        }
      : { x: 0, y: 0 };
  }

  get topLeftPos(): Coordinate2D {
    if (this.dragging === 'topLeft') {
      return;
    }
    return this.cropArea ? { x: this.cropArea.topLeft.x, y: this.cropArea.topLeft.y } : { x: 0, y: 0 };
  }

  get topPos(): Coordinate2D {
    if (this.dragging === 'top') {
      return;
    }
    return this.cropArea
      ? {
          x: this.cropArea.topLeft.x + this.cropArea.width / 2 - this.handleSize / 2,
          y: this.cropArea.topLeft.y,
        }
      : { x: 0, y: 0 };
  }

  get topRightPos(): Coordinate2D {
    if (this.dragging === 'topRight') {
      return;
    }
    return this.cropArea ? { x: this.cropArea.topRight.x - this.handleSize, y: this.cropArea.topRight.y } : { x: 0, y: 0 };
  }

  get rightPos(): Coordinate2D {
    if (this.dragging === 'right') {
      return;
    }
    return this.cropArea
      ? {
          x: this.cropArea.topRight.x - this.handleSize,
          y: this.cropArea.topRight.y + this.cropArea.height / 2 - this.handleSize / 2,
        }
      : { x: 0, y: 0 };
  }

  get bottomRightPos(): Coordinate2D {
    if (this.dragging === 'bottomRight') {
      return;
    }
    return this.cropArea
      ? {
          x: this.cropArea.bottomRight.x - this.handleSize,
          y: this.cropArea.bottomRight.y - this.handleSize,
        }
      : { x: 0, y: 0 };
  }

  get bottomPos(): Coordinate2D {
    if (this.dragging === 'bottom') {
      return;
    }
    return this.cropArea
      ? {
          x: this.cropArea.bottomLeft.x + this.cropArea.width / 2 - this.handleSize / 2,
          y: this.cropArea.bottomLeft.y - this.handleSize,
        }
      : { x: 0, y: 0 };
  }

  get bottomLeftPos(): Coordinate2D {
    if (this.dragging === 'bottomLeft') {
      return;
    }
    return this.cropArea
      ? { x: this.cropArea.bottomLeft.x, y: this.cropArea.bottomLeft.y - this.handleSize }
      : {
          x: 0,
          y: 0,
        };
  }

  constructor() {}

  @ViewChild('myimg') img: ElementRef<HTMLImageElement>;
  @ViewChild('overlay') overlay: ElementRef<HTMLDivElement>;
  @ViewChild('left') leftelement: ElementRef<HTMLDivElement>;
  @ViewChild('topleft') topleftelement: ElementRef<HTMLDivElement>;
  @ViewChild('top') topelement: ElementRef<HTMLDivElement>;
  @ViewChild('topright') toprightelement: ElementRef<HTMLDivElement>;
  @ViewChild('right') rightelement: ElementRef<HTMLDivElement>;
  @ViewChild('bottomright') bottomrightelement: ElementRef<HTMLDivElement>;
  @ViewChild('bottom') bottomelement: ElementRef<HTMLDivElement>;
  @ViewChild('bottomleft') bottomleftelement: ElementRef<HTMLDivElement>;
  @ViewChild('rectangle') rectangle: ElementRef<HTMLDivElement>;

  handleSize: number = 16;

  minWidth: number = this.handleSize * 2;
  minHeight: number = this.handleSize * 2;

  maxWidth: number;
  maxHeight: number;

  cropArea: Rect;
  dragging?: Corner;
  moveDragging: boolean;

  private _initialClipping: Clipping;

  private _clipping: Clipping;
  @Output()
  clippingChange: EventEmitter<Clipping> = new EventEmitter<Clipping>();

  _image: FixedAspectRatioImageDTO;

  private setClipping(value: Clipping) {
    this._clipping = value;
    this.clippingChange.emit(this._clipping);
  }

  ngOnInit(): void {}

  initializeDimensions(): void {
    const width: number = this.img.nativeElement.width;
    const height: number = this.img.nativeElement.height;
    this.cropArea = new Rect({ x: 0, y: 0, width, height });
    // this.cropArea.forceAspectRatio = {ratio: {width: 4, height: 3}, maxSize: {width: width, height: height}};
    this.maxWidth = width;
    this.maxHeight = height;
    this.refreshDimensionsFromClipping();
  }

  onResized(event: ResizedEvent) {
    this.refreshDimensions(
      { width: event.oldRect.width, height: event.oldRect.height },
      {
        width: event.newRect.width,
        height: event.newRect.height,
      }
    );
  }

  refreshDimensions(oldBounds: Bounds, newBounds: Bounds): void {
    if (this.cropArea && oldBounds?.width && oldBounds?.height) {
      const width: number = this.img.nativeElement.width;
      const height: number = this.img.nativeElement.height;

      this.cropArea.scaleXY(width / this.maxWidth, height / this.maxHeight);

      this.maxWidth = width;
      this.maxHeight = height;

      this.refitRectangle();
    }
  }

  error(): void {}

  refreshDimensionsFromClipping(): void {
    if (
      this.clipping?.clippingTLX >= 0 &&
      this.clipping?.clippingTLY >= 0 &&
      this.clipping?.clippingBRX >= 0 &&
      this.clipping?.clippingBRY >= 0 &&
      this.cropArea
    ) {
      const factorX: number = this.img.nativeElement.naturalWidth / this.cropArea.width;
      const factorY: number = this.img.nativeElement.naturalHeight / this.cropArea.height;
      this.cropArea.x = Math.round(this.clipping.clippingTLX / factorX);
      this.cropArea.y = Math.round(this.clipping.clippingTLY / factorY);
      this.cropArea.bottomRight = { x: this.clipping.clippingBRX / factorX, y: this.clipping.clippingBRY / factorY };
    }
  }

  refreshClippingFromDimensions(): void {
    const factorX: number = this.img.nativeElement.naturalWidth / this.img.nativeElement.width;
    const factorY: number = this.img.nativeElement.naturalHeight / this.img.nativeElement.height;
    const clipping: Clipping = {
      clippingTLX: Math.round(this.cropArea.x * factorX),
      clippingTLY: Math.round(this.cropArea.y * factorY),
      clippingBRX: Math.round(this.cropArea.bottomRight.x * factorX),
      clippingBRY: Math.round(this.cropArea.bottomRight.y * factorY),
    };

    this.setClipping(clipping);
  }

  moveDragStarted(event: CdkDragStart): void {
    if (this.cropArea) {
      this.moveDragging = true;
    }
  }

  moveDragMoved(event: CdkDragMove): void {
    if (this.cropArea) {
      const fromLeftOfDraggedElement = event.pointerPosition.x - this.rectangle.nativeElement.getBoundingClientRect().left;
      const fromTopOfDraggedElement = event.pointerPosition.y - this.rectangle.nativeElement.getBoundingClientRect().top;

      const xRelativeToParent =
        event.pointerPosition.x - fromLeftOfDraggedElement - this.overlay.nativeElement.getBoundingClientRect().left;
      const yRelativeToParent = event.pointerPosition.y - fromTopOfDraggedElement - this.overlay.nativeElement.getBoundingClientRect().top;

      this.cropArea.position = { x: xRelativeToParent, y: yRelativeToParent };
    }
  }

  moveDragEnded(event: CdkDragEnd): void {
    if (this.cropArea) {
      this.moveDragging = false;
      this.refitRectangle(true);
    }
  }

  resizeDragStarted(event: CdkDragStart, which: Corner): void {
    if (this.cropArea) {
      this.dragging = which;
    }
  }

  resizeDragMoved(event: CdkDragMove, which: Corner): void {
    if (this.cropArea) {
      const handle: ElementRef<HTMLDivElement> = this.findHandleByName(which);

      const fromLeftOfDraggedElement = event.pointerPosition.x - handle.nativeElement.getBoundingClientRect().left;
      const fromTopOfDraggedElement = event.pointerPosition.y - handle.nativeElement.getBoundingClientRect().top;

      const xRelativeToParent =
        event.pointerPosition.x - fromLeftOfDraggedElement - this.overlay.nativeElement.getBoundingClientRect().left;
      const yRelativeToParent = event.pointerPosition.y - fromTopOfDraggedElement - this.overlay.nativeElement.getBoundingClientRect().top;

      this.resizeUpdateCoordinates(which, { x: xRelativeToParent, y: yRelativeToParent });
    }
  }

  resizeDragEnded(event: CdkDragEnd, which: Corner): void {
    if (this.cropArea) {
      this.dragging = null;

      this.resizeUpdateCoordinates(which, {
        x: event.source.getFreeDragPosition().x,
        y: event.source.getFreeDragPosition().y,
      });
      this.refitRectangle(true);
    }
  }

  findHandleByName(which: Corner): ElementRef<HTMLDivElement> {
    switch (which) {
      case 'left':
        return this.leftelement;
      case 'topLeft':
        return this.topleftelement;
      case 'top':
        return this.topelement;
      case 'topRight':
        return this.toprightelement;
      case 'right':
        return this.rightelement;
      case 'bottomRight':
        return this.bottomrightelement;
      case 'bottom':
        return this.bottomelement;
      case 'bottomLeft':
        return this.bottomleftelement;
      default:
        return;
    }
  }

  resizeUpdateCoordinates(which: Corner, position: Coordinate2D) {
    switch (which) {
      case 'left':
        this.cropArea.topLeft = { x: position.x, y: this.cropArea.topLeft.y };
        break;
      case 'topLeft':
        this.cropArea.topLeft = { x: position.x, y: position.y };
        break;
      case 'top':
        this.cropArea.topLeft = { x: this.cropArea.topLeft.x, y: position.y };
        break;
      case 'topRight':
        this.cropArea.topRight = { x: position.x + this.handleSize, y: position.y };
        break;
      case 'right':
        this.cropArea.topRight = { x: position.x + this.handleSize, y: this.cropArea.topRight.y };
        break;
      case 'bottomRight':
        this.cropArea.bottomRight = { x: position.x + this.handleSize, y: position.y + this.handleSize };
        break;
      case 'bottom':
        this.cropArea.bottomRight = { x: this.cropArea.bottomRight.x, y: position.y };
        break;
      case 'bottomLeft':
        this.cropArea.bottomLeft = { x: position.x, y: position.y + this.handleSize };
        break;
    }

    this.refitRectangle(false);
  }

  refitRectangle(refreshClipping: boolean = false): void {
    if (this.cropArea.width < this.minWidth) {
      this.cropArea.width = this.minWidth;
    }
    if (this.cropArea.height < this.minHeight) {
      this.cropArea.height = this.minHeight;
    }
    if (this.cropArea.height > this.maxHeight) {
      this.cropArea.height = this.maxHeight;
    }
    if (this.cropArea.width > this.maxWidth) {
      this.cropArea.width = this.maxWidth;
    }

    if (refreshClipping) {
      this.refreshClippingFromDimensions();
    }
  }
}


